import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { AlbumDetail } from '@photos/shared';
import { useAlbum, useAlbums, useDeleteAlbum, useUpdateAlbum } from '../../api/hooks';
import { AlbumCombobox, type AlbumValue } from '../../components/AlbumPicker';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { TagChip } from '../../components/TagChip';
import { TagEditor } from '../../components/TagEditor';
import { useEscape } from '../../hooks/useEscape';
import { NO_FILTERS, type ActivityFilters } from '../../lib/activityFilters';
import { fmtCount } from '../../lib/format';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toasts';
import { AlbumActivity } from './ActivityPage';
import { AlbumPeople } from './AlbumPeople';
import { albumCounts } from './AlbumsPage';
import { StudioPhotosView } from './StudioPhotosView';
import { ReleaseDialog } from './studioUi';

type Dialog = 'release' | 'delete' | null;

/** Album name, renamed in place: Enter saves, Escape cancels. */
function RenameField({ album, onDone }: { album: AlbumDetail; onDone(): void }) {
  const [name, setName] = useState(album.name);
  const update = useUpdateAlbum(album.id);
  const toast = useToast();
  const save = () => {
    const next = name.trim();
    if (!next || next === album.name) return onDone();
    update.mutate(
      { name: next },
      {
        onSuccess: () => {
          onDone();
          toast({ icon: 'check', title: `Renamed to ${next}` });
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t rename the album', sub: e.message }),
      },
    );
  };
  useEscape(onDone);
  return (
    <input
      className="rename"
      aria-label="Album name"
      autoFocus
      maxLength={100}
      value={name}
      disabled={update.isPending}
      onChange={(e) => setName(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === 'Enter') save();
      }}
    />
  );
}

function Description({ album }: { album: AlbumDetail }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(album.description);
  const update = useUpdateAlbum(album.id);
  const toast = useToast();
  if (!album.can.manage && !album.description) return null;
  const save = () =>
    update.mutate(
      { description: text.trim() },
      {
        onSuccess: () => setEditing(false),
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t save the description', sub: e.message }),
      },
    );
  return (
    <div className="album-desc">
      {editing ? (
        <>
          <input className="inp sm" aria-label="Album description" autoFocus maxLength={500} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} />
          <button type="button" className="btn sm primary" onClick={save} disabled={update.isPending}>
            Save
          </button>
          <button type="button" className="btn sm ghost" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </>
      ) : (
        <>
          <span>{album.description || <i style={{ color: 'var(--muted)' }}>No description</i>}</span>
          {album.can.manage && (
            <button type="button" className="btn sm ghost" aria-label="Edit description" onClick={() => setEditing(true)}>
              <Icon name="pencil" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** "Album tags": the default tags every upload into this album starts with (editable by people who manage albums). */
function AlbumTags({ album }: { album: AlbumDetail }) {
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState(album.defaultTags);
  const update = useUpdateAlbum(album.id);
  const toast = useToast();
  if (!album.can.manage && !album.defaultTags.length) return null;
  const save = () =>
    update.mutate(
      { defaultTags: tags },
      {
        onSuccess: () => {
          setEditing(false);
          toast({ icon: 'tag', title: 'Album tags saved', sub: 'New uploads into this album start with them.' });
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t save the album tags', sub: e.message }),
      },
    );
  return (
    <div className="album-tags">
      <span className="sl">Album tags</span>
      {editing ? (
        <>
          <TagEditor id="albumTags" tags={tags} onChange={setTags} placeholder="Add tag…" />
          <button type="button" className="btn sm primary" onClick={save} disabled={update.isPending}>
            Save
          </button>
          <button
            type="button"
            className="btn sm ghost"
            onClick={() => {
              setTags(album.defaultTags);
              setEditing(false);
            }}
          >
            Cancel
          </button>
        </>
      ) : (
        <>
          {album.defaultTags.length ? album.defaultTags.map((t) => <TagChip key={t} tag={t} />) : <i style={{ color: 'var(--muted)' }}>None</i>}
          {album.can.manage && (
            <button type="button" className="btn sm ghost" onClick={() => setEditing(true)}>
              <Icon name="pencil" />
              Edit
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** Deleting: allowed right away without home photos (references are dropped); otherwise choose where they move. */
function DeleteDialog({ album, onClose }: { album: AlbumDetail; onClose(): void }) {
  const albums = useAlbums();
  const del = useDeleteAlbum();
  const navigate = useNavigate();
  const toast = useToast();
  const [target, setTarget] = useState<AlbumValue>(null);
  const needsTarget = album.homeCount > 0;
  const confirm = () =>
    del.mutate(
      { id: album.id, moveTo: target && 'id' in target ? target.id : undefined },
      {
        onSuccess: (r) => {
          onClose();
          toast({ icon: 'trash', title: `Deleted ${album.name}`, sub: r.moved ? `${fmtCount(r.moved)} photos moved to ${target && 'id' in target ? target.name : 'another album'}.` : undefined });
          navigate('/studio/albums');
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t delete the album', sub: e.message }),
      },
    );
  return (
    <Modal onClose={onClose} labelledBy="del-title">
      <h3 id="del-title">Delete {album.name}?</h3>
      <p className="help">
        {needsTarget
          ? `${fmtCount(album.homeCount)} photos live in this album. Choose the album they move to; photos only added here from other albums just leave.`
          : 'No photos live in this album. Photos added here from other albums stay in their own albums.'}
      </p>
      {needsTarget && (
        <div className="field">
          <AlbumCombobox albums={albums.data?.items ?? []} value={target} onChange={setTarget} allowCreate={false} excludeIds={[album.id]} placeholder="Move its photos to…" />
        </div>
      )}
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary" disabled={del.isPending || (needsTarget && !(target && 'id' in target))} onClick={confirm}>
          Delete album
        </button>
      </div>
    </Modal>
  );
}

/** `/studio/albums/:id`: header (name, description, counts, actions), toolbar, grid, selection bar and lightbox. */
export function AlbumPage() {
  const { id = '' } = useParams();
  const { access } = useSession();
  const navigate = useNavigate();
  const album = useAlbum(id);
  const [renaming, setRenaming] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [drawer, setDrawer] = useState<'activity' | 'people' | null>(null);
  const [activityFilters, setActivityFilters] = useState<ActivityFilters>(NO_FILTERS);
  useEscape(() => setDrawer(null), !!drawer && !dialog);
  const toggle = (d: 'activity' | 'people') => setDrawer((o) => (o === d ? null : d));

  if (album.isError)
    return (
      <div className="empty">
        <h3>Album not found</h3>
        It may have been deleted, or you don’t have access to it.
      </div>
    );
  const a = album.data;
  if (!a)
    return (
      <div className="sentinel">
        <div className="spinner" />
      </div>
    );

  const heroActions = (
    <div className="album-acts">
      {a.can.upload && (
        <button type="button" className="btn sm" title="Upload to this album" onClick={() => navigate(`/studio/upload?album=${encodeURIComponent(a.id)}`)}>
          <Icon name="upload" />
          Upload to this album
        </button>
      )}
      {a.can.release && a.counts.unreleased > 0 && (
        <button type="button" className="btn sm" title="Release album" onClick={() => setDialog('release')}>
          <Icon name="globe" />
          Release album
        </button>
      )}
      {access.canManageAccess && (
        <button type="button" className="btn sm" title="People with access" aria-expanded={drawer === 'people'} onClick={() => toggle('people')}>
          <Icon name="users" />
          People
        </button>
      )}
      <button type="button" className="btn sm" title="Activity" aria-expanded={drawer === 'activity'} onClick={() => toggle('activity')}>
        <Icon name="undo" />
        Activity
      </button>
      {a.can.manage && (
        <button type="button" className="btn sm" aria-label="Delete album" title="Delete album" onClick={() => setDialog('delete')}>
          <Icon name="trash" />
        </button>
      )}
    </div>
  );

  return (
    <>
      <StudioPhotosView
        key={a.id}
        album={a}
        title={a.name}
        heading={
          renaming ? (
            <RenameField album={a} onDone={() => setRenaming(false)} />
          ) : a.can.manage ? (
            <button type="button" className="title-edit" title="Rename album" onClick={() => setRenaming(true)}>
              {a.name}
              <Icon name="pencil" />
            </button>
          ) : undefined
        }
        heroSub={[albumCounts(a, access.seesRestricted), ...(a.homeCount < a.total ? [`${fmtCount(a.total - a.homeCount)} added from other albums`] : [])]}
        heroActions={heroActions}
        empty={a.can.upload ? 'No photos yet. Upload to this album to get started.' : 'No photos you can see yet.'}
      >
        <Description album={a} />
        <AlbumTags album={a} />
        {a.collaborators.length > 0 && (
          <div className="notice">
            <Icon name="users" />
            <span>
              People with access: {a.collaborators.map((c) => c.name).join(', ')}
              {access.canManageAccess && (
                <>
                  {' · '}
                  <button type="button" className="linkbtn" onClick={() => setDrawer('people')}>
                    Manage
                  </button>
                </>
              )}
            </span>
          </div>
        )}
      </StudioPhotosView>
      {drawer === 'people' && <AlbumPeople album={a} onClose={() => setDrawer(null)} />}
      {drawer === 'activity' && (
        <aside className="drawer" aria-label={`Activity in ${a.name}`}>
          <div className="drawer-head">
            <b>Activity</b>
            <button type="button" className="btn ghost sm" aria-label="Close activity" onClick={() => setDrawer(null)}>
              <Icon name="x" />
            </button>
          </div>
          <AlbumActivity albumId={a.id} filters={activityFilters} onChange={setActivityFilters} />
        </aside>
      )}
      {dialog === 'release' && <ReleaseDialog target={{ album: a }} onClose={() => setDialog(null)} />}
      {dialog === 'delete' && <DeleteDialog album={a} onClose={() => setDialog(null)} />}
    </>
  );
}
